import { useCallback } from 'react';
import type { ReactNode } from 'react';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { useTracking } from 'react-tracking';
import type { ButtonRenderProps } from '@/components/Dropdown';
import { Dropdown } from '@/components/Dropdown';
import { Button as DeprecatedButton, Grid } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core/next';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import TilesSolid from '@simplywallst/ui-core/icons/TilesSolid';
import ListSolid from '@simplywallst/ui-core/icons/ListSolid';
import FilterOutline from '@simplywallst/ui-core/icons/FilterOutline';
import { push } from 'redux-first-router';
import {
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
} from '@/constants/routes';
import { DropdownWrapper, TotalRecords, ButtonWrapper } from './styled';
import { useMarketOptions } from '@/hooks/discover/useMarketOptions';
import useIndustryOptions from '../../hooks/useIndustryOptions';
import useSortByOptions from '../../hooks/useSortByOptions';
import { MarketIndustryFilter } from '@/components/Header';
import { getStocksPath } from '../../utils';
import { DEFAULT_INDUSTRY, DEFAULT_SORT_BY } from '../../constants';

type SortBy =
  | 'market-cap-large'
  | 'market-cap-small'
  | 'top-gainers'
  | 'biggest-losers'
  | 'growth'
  | 'dividend-yield-high';

type StocksView = 'tile' | 'table';

export interface Props {
  market: PrimaryCountryISOElseGlobal;
  industry: string | 'any';
  sortBy?: SortBy;
  stocksView: StocksView;
  totalRecords?: number;
  onViewSelect: (view: StocksView) => void;
  onAdvancedFilterClick: () => void;
  children: ReactNode;
}

interface NavigateTo {
  market: PrimaryCountryISOElseGlobal;
  industry?: string | 'any';
  sortBy: SortBy;
}

const Header = ({
  children,
  market,
  industry,
  sortBy = DEFAULT_SORT_BY,
  stocksView,
  totalRecords,
  onViewSelect,
  onAdvancedFilterClick,
}: Props) => {
  const { trackEvent } = useTracking();

  const marketOptions = useMarketOptions(
    true,
    industry !== DEFAULT_INDUSTRY
      ? ROUTE_STOCKS_MARKET_INDUSTRY
      : ROUTE_STOCKS_MARKET,
    {
      industry,
      sortBy,
    }
  );
  const industryOptions = useIndustryOptions(true, market, {
    sortBy,
  });
  const sortByOptions = useSortByOptions(market, industry);

  const dropdownButton = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
      active,
    }: ButtonRenderProps) => {
      return (
        <ButtonWrapper active={active}>
          <DeprecatedButton
            styleType="passiveLarge"
            alignIcon="right"
            onBlur={onBlur}
            onFocus={onFocus}
            onClick={onClick}
            ref={innerRef}
            data-cy-id={name}
          >
            {value}
            <ChevronDownSmallSolid />
          </DeprecatedButton>
        </ButtonWrapper>
      );
    },
    []
  );

  const handleTableView = useCallback(() => {
    onViewSelect('table');
    trackEvent({
      action: 'click',
      subject: 'table-view',
    });
  }, [onViewSelect, trackEvent]);

  const handleTileView = useCallback(() => {
    onViewSelect('tile');
    trackEvent({
      action: 'click',
      subject: 'tile-view',
    });
  }, [onViewSelect, trackEvent]);

  const navigateTo = useCallback(({ market, industry, sortBy }: NavigateTo) => {
    const path = getStocksPath({
      market,
      industry,
      sortBy,
      excludePageQuery: true,
    });
    if (typeof path === 'undefined') return;
    try {
      push(path);
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const handlerMarketChange = useCallback(
    (market: string) => {
      navigateTo({
        market: market as PrimaryCountryISOElseGlobal,
        industry,
        sortBy,
      });
    },
    [industry, sortBy, navigateTo]
  );

  const handleIndustryChange = useCallback(
    (industry: string) => {
      navigateTo({
        market,
        industry,
        sortBy,
      });
    },
    [market, sortBy, navigateTo]
  );

  const handleSortByChange = useCallback(
    (sortBy: string) => {
      navigateTo({
        market,
        industry,
        sortBy: sortBy as SortBy,
      });
    },
    [market, industry, navigateTo]
  );

  return (
    <header className="border-0 border-b border-solid border-b-soft px-4 py-2 lg:px-0 lg:pb-1 lg:pt-3">
      <MarketIndustryFilter
        marketOptions={marketOptions}
        industryOptions={industryOptions}
        selectedMarket={market}
        selectedIndustry={industry}
        onMarketChange={handlerMarketChange}
        onIndustryChange={handleIndustryChange}
        hideIndustryFilter={false}
      >
        <Button
          variant="subtle"
          size="large"
          data-cy-id="advanced-filters-button"
          onClick={onAdvancedFilterClick}
          className="ml-auto h-x4" // Align height with the dropdowns
        >
          Advanced Filters
          <FilterOutline />
        </Button>
      </MarketIndustryFilter>
      <Grid row="2" className="py-3 lg:py-5">
        {children}
      </Grid>
      <Grid templateColumns="auto auto" templateColumnsMd="250px auto 250px">
        <DropdownWrapper>
          <Dropdown
            name="stock-sorting"
            displayType="block"
            align="left"
            options={sortByOptions}
            value={sortBy}
            onSelect={handleSortByChange}
            buttonRender={dropdownButton}
          />
        </DropdownWrapper>
        <Grid
          justifyContent="center"
          alignContent="center"
          row="2"
          column="span 2"
          rowMd="1"
          columnMd="2"
        >
          {totalRecords && (
            <TotalRecords>
              {totalRecords.toLocaleString()} companies
            </TotalRecords>
          )}
        </Grid>
        <Grid display="grid" templateColumns="auto auto" justifyContent="end">
          <DeprecatedButton
            data-cy-id="table-view"
            styleType={stocksView === 'tile' ? 'passiveLarge' : 'loudLarge'}
            marginRight
            onClick={handleTableView}
          >
            <ListSolid />
          </DeprecatedButton>
          <DeprecatedButton
            data-cy-id="tile-view"
            styleType={stocksView === 'table' ? 'passiveLarge' : 'loudLarge'}
            onClick={handleTileView}
          >
            <TilesSolid />
          </DeprecatedButton>
        </Grid>
      </Grid>
    </header>
  );
};

export default Header;
